import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArchiveOutlined,
  ArrowBack,
  DriveFileRenameOutline,
  EditNoteOutlined,
  ExitToApp,
  MoreVert,
  PersonAddAlt1Outlined,
  UnarchiveOutlined,
} from "@mui/icons-material";
import {
  addChatMembers,
  fetchChatMessages,
  joinChatConversation,
  leaveChatConversation,
  markChatConversationRead,
  sendChatMessage,
  updateChatConversation,
} from "../../api/ProcurementClient";
import { ChatConversation, ChatMessage } from "../../models/Chat";
import { Role } from "../../models/User";
import ConfirmDialog from "../ConfirmDialog";
import { ConversationAvatar } from "./ChatAvatar";
import { useChat } from "./ChatProvider";
import Composer, { ComposerSubmit } from "./Composer";
import MessageItem from "./MessageItem";
import { AddPeopleDialog, TextPromptDialog } from "./NewChatDialog";
import { TeamsConversationBadge } from "./TeamsConnection";
import {
  CHAT_FAB_GUTTER,
  ChatDirectory,
  conversationTitle,
  directPartnerId,
  formatDayDivider,
  isSameChatDay,
  userDisplayName,
} from "./chatUtils";
import {
  appendMessage,
  applyReaction,
  isCompactFollowUp,
  replaceMessage,
} from "./messageActions";
import { useMessageActions } from "./useMessageActions";

interface MessagePaneProps {
  conversation: ChatConversation;
  directory: ChatDirectory;
  activeThreadId: number | null;
  onOpenThread: (messageId: number) => void;
  onLeft: () => void;
  /** Rightmost pane: keep the composer clear of the fixed RC voice button. */
  reserveFabSpace: boolean;
  /** Sidebar mode: back arrow to the conversation list. */
  onBack?: () => void;
  extraActions?: React.ReactNode;
}

const NEAR_BOTTOM_PX = 120;
const LOAD_OLDER_PX = 80;
const MARK_READ_DEBOUNCE_MS = 1500;

type PromptKind = "rename" | "description" | null;

const MessagePane: React.FC<MessagePaneProps> = ({
  conversation,
  directory,
  activeThreadId,
  onOpenThread,
  onLeft,
  reserveFabSpace,
  onBack,
  extraActions,
}) => {
  const { me, staff, usersById } = directory;
  const { subscribe, clearUnread, upsertConversation, refreshConversations } = useChat();
  const conversationId = conversation.id;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [prompt, setPrompt] = useState<PromptKind>(null);
  const [addPeopleOpen, setAddPeopleOpen] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const preserveFromHeightRef = useRef<number | null>(null);
  const loadTokenRef = useRef(0);

  // Badges clear immediately; the server read receipt is batched so a busy chat
  // doesn't send one per incoming message.
  const readTimerRef = useRef<number | undefined>(undefined);
  const markRead = useCallback(() => {
    if (!conversation.joined || document.visibilityState !== "visible") return;
    clearUnread(conversationId);
    window.clearTimeout(readTimerRef.current);
    readTimerRef.current = window.setTimeout(() => {
      readTimerRef.current = undefined;
      void markChatConversationRead(conversationId).catch(() => {});
    }, MARK_READ_DEBOUNCE_MS);
  }, [conversationId, conversation.joined, clearUnread]);

  useEffect(
    () => () => {
      if (readTimerRef.current === undefined) return;
      window.clearTimeout(readTimerRef.current);
      readTimerRef.current = undefined;
      void markChatConversationRead(conversationId).catch(() => {});
    },
    [conversationId]
  );

  const loadLatest = useCallback(async () => {
    const token = ++loadTokenRef.current;
    try {
      const page = await fetchChatMessages(conversationId);
      if (token !== loadTokenRef.current) return;
      stickToBottomRef.current = true;
      setMessages(page.messages);
      setHasMore(page.hasMore);
      setError(null);
    } catch (err) {
      if (token === loadTokenRef.current) {
        setError(err instanceof Error ? err.message : "Failed to load messages");
      }
    }
  }, [conversationId]);

  useEffect(() => {
    setMessages([]);
    setHasMore(false);
    setLoading(true);
    setError(null);
    setActionError(null);
    void loadLatest().finally(() => setLoading(false));
  }, [loadLatest]);

  useEffect(() => {
    if (!loading) markRead();
  }, [loading, markRead]);

  useEffect(() => {
    if (conversation.unreadCount > 0) markRead();
  }, [conversation.unreadCount, markRead]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") markRead();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [markRead]);

  useEffect(
    () =>
      subscribe((event) => {
        if (event.type === "message" && event.message.conversationId === conversationId) {
          if (!event.message.parentMessageId) {
            setMessages((prev) => appendMessage(prev, event.message));
          }
          markRead();
        } else if (event.type === "message-updated" && event.message.conversationId === conversationId) {
          if (!event.message.parentMessageId) {
            setMessages((prev) => replaceMessage(prev, event.message));
          }
        } else if (event.type === "reaction" && event.reaction.conversationId === conversationId) {
          setMessages((prev) => applyReaction(prev, event.reaction));
        } else if (event.type === "connected") {
          void loadLatest();
        }
      }),
    [subscribe, conversationId, markRead, loadLatest]
  );

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (preserveFromHeightRef.current != null) {
      el.scrollTop = el.scrollHeight - preserveFromHeightRef.current;
      preserveFromHeightRef.current = null;
    } else if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const loadOlder = useCallback(async () => {
    if (loadingOlder || !hasMore || messages.length === 0) return;
    setLoadingOlder(true);
    try {
      const page = await fetchChatMessages(conversationId, messages[0].id);
      preserveFromHeightRef.current = scrollRef.current?.scrollHeight ?? null;
      setMessages((prev) => {
        const known = new Set(prev.map((m) => m.id));
        return [...page.messages.filter((m) => !known.has(m.id)), ...prev];
      });
      setHasMore(page.hasMore);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to load older messages");
    } finally {
      setLoadingOlder(false);
    }
  }, [conversationId, hasMore, loadingOlder, messages]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    if (el.scrollTop < LOAD_OLDER_PX) void loadOlder();
  };

  const send = async (input: ComposerSubmit) => {
    const message = await sendChatMessage(conversationId, { ...input, parentMessageId: null });
    stickToBottomRef.current = true;
    setMessages((prev) => appendMessage(prev, message));
    if (!conversation.joined) {
      void refreshConversations();
    }
  };

  const actions = useMessageActions(
    me.id,
    (updated) => setMessages((prev) => replaceMessage(prev, updated)),
    setActionError
  );

  const runAction = async (action: () => Promise<void>) => {
    setActionError(null);
    try {
      await action();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Action failed");
    }
  };

  const title = conversationTitle(conversation, me.id, usersById);
  const isChannel = conversation.type === "channel";
  const isGroup = conversation.type === "group";
  const canManageChannel = isChannel && (conversation.createdById === me.id || me.role === Role.ADMIN);
  const archived = Boolean(conversation.archivedAt);
  const partner = directPartnerId(conversation, me.id);

  let subtitle = "";
  if (isChannel) {
    const count = conversation.memberIds.length;
    subtitle = `${count} ${count === 1 ? "member" : "members"}${conversation.description ? ` · ${conversation.description}` : ""}`;
  } else if (isGroup) {
    subtitle = conversation.memberIds.map((id) => (id === me.id ? "You" : userDisplayName(usersById.get(id)))).join(", ");
  } else if (partner) {
    subtitle = usersById.get(partner)?.email ?? "";
  }

  const menuItems: React.ReactNode[] = [];
  if (isGroup) {
    menuItems.push(
      <MenuItem key="add" onClick={() => { setMenuAnchor(null); setAddPeopleOpen(true); }}>
        <ListItemIcon><PersonAddAlt1Outlined fontSize="small" /></ListItemIcon>
        Add people
      </MenuItem>,
      <MenuItem key="rename" onClick={() => { setMenuAnchor(null); setPrompt("rename"); }}>
        <ListItemIcon><DriveFileRenameOutline fontSize="small" /></ListItemIcon>
        Rename group
      </MenuItem>
    );
  }
  if (canManageChannel) {
    menuItems.push(
      <MenuItem key="rename" onClick={() => { setMenuAnchor(null); setPrompt("rename"); }}>
        <ListItemIcon><DriveFileRenameOutline fontSize="small" /></ListItemIcon>
        Rename channel
      </MenuItem>,
      <MenuItem key="description" onClick={() => { setMenuAnchor(null); setPrompt("description"); }}>
        <ListItemIcon><EditNoteOutlined fontSize="small" /></ListItemIcon>
        Edit description
      </MenuItem>,
      <MenuItem
        key="archive"
        onClick={() => {
          setMenuAnchor(null);
          void runAction(async () =>
            upsertConversation(await updateChatConversation(conversationId, { archived: !archived }))
          );
        }}
      >
        <ListItemIcon>
          {archived ? <UnarchiveOutlined fontSize="small" /> : <ArchiveOutlined fontSize="small" />}
        </ListItemIcon>
        {archived ? "Restore channel" : "Archive channel"}
      </MenuItem>
    );
  }
  if ((isGroup || isChannel) && conversation.joined) {
    menuItems.push(
      <MenuItem key="leave" onClick={() => { setMenuAnchor(null); setConfirmLeave(true); }} sx={{ color: "error.main" }}>
        <ListItemIcon><ExitToApp fontSize="small" color="error" /></ListItemIcon>
        Leave {isChannel ? "channel" : "group"}
      </MenuItem>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "#f5f5f5", minWidth: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={onBack ? 1 : 1.5}
        sx={{
          pl: onBack ? 0.5 : 2.5,
          pr: onBack ? 1 : 2.5,
          py: 1.25,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {onBack && (
          <Tooltip title="All chats">
            <IconButton size="small" onClick={onBack} aria-label="Back to all chats">
              <ArrowBack fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <ConversationAvatar conversation={conversation} meId={me.id} usersById={usersById} size={36} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {title}
            {archived && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (archived)
              </Typography>
            )}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {subtitle}
            </Typography>
          )}
        </Box>
        <TeamsConversationBadge conversation={conversation} />
        {isChannel && !conversation.joined && !archived && (
          <Button
            variant="contained"
            size="small"
            onClick={() =>
              void runAction(async () => upsertConversation(await joinChatConversation(conversationId)))
            }
          >
            Join channel
          </Button>
        )}
        {menuItems.length > 0 && (
          <Tooltip title="More options">
            <IconButton onClick={(event) => setMenuAnchor(event.currentTarget)}>
              <MoreVert />
            </IconButton>
          </Tooltip>
        )}
        <Menu open={Boolean(menuAnchor)} anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)}>
          {menuItems}
        </Menu>
        {extraActions}
      </Stack>

      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)} sx={{ borderRadius: 0 }}>
          {actionError}
        </Alert>
      )}

      <Box ref={scrollRef} onScroll={onScroll} sx={{ flex: 1, minHeight: 0, overflowY: "auto", py: 1.5 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : messages.length === 0 ? (
          <Stack alignItems="center" sx={{ pt: 8, px: 3, textAlign: "center" }} gap={1}>
            <ConversationAvatar conversation={conversation} meId={me.id} usersById={usersById} size={64} />
            <Typography variant="h6">{title}</Typography>
            <Typography color="text.secondary" variant="body2">
              {isChannel
                ? "This is the start of the channel. Post something to get the conversation going."
                : "No messages yet. Say hello!"}
            </Typography>
          </Stack>
        ) : (
          <>
            {loadingOlder && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {!hasMore && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", pb: 1 }}>
                Beginning of conversation
              </Typography>
            )}
            {messages.map((message, index) => {
              const prev = messages[index - 1];
              const newDay = !prev || !isSameChatDay(prev.createdAt, message.createdAt);
              return (
                <React.Fragment key={message.id}>
                  {newDay && (
                    <Divider sx={{ my: 1.5, px: 3, "&::before, &::after": { borderColor: "grey.300" } }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {formatDayDivider(message.createdAt)}
                      </Typography>
                    </Divider>
                  )}
                  <Box
                    sx={{
                      bgcolor: activeThreadId === message.id ? "rgba(98,100,167,0.08)" : undefined,
                    }}
                  >
                    <MessageItem
                      message={message}
                      meId={me.id}
                      usersById={usersById}
                      mentionCandidates={staff}
                      compact={!newDay && isCompactFollowUp(prev, message)}
                      onOpenThread={onOpenThread}
                      onToggleReaction={actions.onToggleReaction}
                      onEdit={actions.onEdit}
                      onDelete={actions.onDelete}
                      onOpenAttachment={actions.onOpenAttachment}
                    />
                  </Box>
                </React.Fragment>
              );
            })}
          </>
        )}
      </Box>

      <Box sx={{ pl: 2.5, pr: reserveFabSpace ? CHAT_FAB_GUTTER : 2.5, pb: 2, pt: 1 }}>
        {archived ? (
          <Alert severity="info">This channel is archived. Restore it to post again.</Alert>
        ) : (
          <Composer
            key={conversationId}
            placeholder={isChannel ? `Post in ${title}` : "Type a message"}
            mentionCandidates={staff}
            usersById={usersById}
            onSubmit={send}
            autoFocus
          />
        )}
      </Box>

      {actions.dialogs}
      <ConfirmDialog
        open={confirmLeave}
        title={`Leave ${isChannel ? "channel" : "group"}?`}
        okLabel="Leave"
        okColor="error"
        cancelColor="inherit"
        onOk={() => {
          setConfirmLeave(false);
          void runAction(async () => {
            await leaveChatConversation(conversationId);
            await refreshConversations();
            onLeft();
          });
        }}
        onCancel={() => setConfirmLeave(false)}
      >
        {isChannel
          ? "You can rejoin from Browse channels at any time."
          : "You will stop receiving messages from this group unless someone adds you back."}
      </ConfirmDialog>
      <TextPromptDialog
        open={prompt !== null}
        title={prompt === "description" ? "Edit channel description" : isChannel ? "Rename channel" : "Rename group"}
        label={prompt === "description" ? "Description" : "Name"}
        initialValue={prompt === "description" ? conversation.description : conversation.name}
        multiline={prompt === "description"}
        required={prompt === "rename" && isChannel}
        onClose={() => setPrompt(null)}
        onSave={async (value) => {
          const changes = prompt === "description" ? { description: value } : { name: value };
          upsertConversation(await updateChatConversation(conversationId, changes));
        }}
      />
      <AddPeopleDialog
        open={addPeopleOpen}
        onClose={() => setAddPeopleOpen(false)}
        staff={staff}
        excludeIds={conversation.memberIds}
        onAdd={async (userIds) => upsertConversation(await addChatMembers(conversationId, userIds))}
      />
    </Box>
  );
};

export default MessagePane;
